/** Rule-based fallback markdown for Executive Overview (Mistral enriches when available). */

export const formatOshNarrative = (shiSummary) => {
  const shi = shiSummary?.school_health_index ?? 0;
  const rag = shiSummary?.rag_status?.status ?? "Amber";
  return `The district's Overall School Health (OSH) stands at ${shi}, categorized as ${rag}.`;
};

/** Normalize Mistral or legacy copy that still references School Health Index (SHI). */
export const normalizeOshTerminology = (text) => {
  if (!text || typeof text !== "string") return text;
  let result = text
    .replace(
      /The district['']s overall School Health Index \(SHI\) stands at/gi,
      "The district's Overall School Health (OSH) stands at"
    )
    .replace(/overall School Health Index \(SHI\)/gi, "Overall School Health (OSH)")
    .replace(/School Health Index \(SHI\)/gi, "Overall School Health (OSH)")
    .replace(/School Health Index/gi, "Overall School Health (OSH)");

  // Mixed SHI/OSH copy can leave "(OSH) (OSH)" after SHI → OSH replacement.
  result = result
    .replace(/Overall School Health\s*\(OSH\)\s*\(OSH\)/gi, "Overall School Health (OSH)")
    .replace(/\(OSH\)\s*\(OSH\)/gi, "(OSH)");

  return result;
};

const buildNarrative = (shi, domains) => {
  const status = shi?.rag_status?.status ?? "Amber";
  const statusLower = status.toLowerCase();
  const teacher = domains.teacher?.index ?? 0;
  const identity = domains.identity?.index ?? 0;
  const infra = domains.infrastructure?.index ?? 0;

  if (teacher < 50 && identity >= 80 && infra >= 80) {
    return `The district is in ${statusLower} status. Infrastructure and identity are strong, while teacher quality remains the highest-impact improvement area.`;
  }
  if (status === "Green") {
    return "The district is performing well across core domains with sustained improvement momentum.";
  }
  if (status === "Red") {
    return "The district needs urgent intervention across multiple domains to restore school health performance.";
  }
  return formatOshNarrative(shi);
};

export const buildExecutiveOverviewMarkdown = ({
  shiSummary,
  domains = {},
  blockRankings = [],
}) => {
  const narrative = buildNarrative(shiSummary, domains);
  const shi = shiSummary?.school_health_index ?? 0;
  const rag = shiSummary?.rag_status?.status ?? "Amber";

  const priorityBlocks = [...(blockRankings || [])]
    .filter((b) => b.rag?.status !== "Green")
    .sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0))
    .slice(0, 5)
    .map((b) => b.block_name)
    .filter(Boolean);

  const blockText = priorityBlocks.length ? priorityBlocks.join(", ") : "priority blocks";

  const rootSignals = [
    `OSH composite is ${shi} (${rag}) across identity, infrastructure, teacher and operational domains.`,
    `Teacher domain index is ${domains.teacher?.index ?? 0}%; identity ${domains.identity?.index ?? 0}%; infrastructure ${domains.infrastructure?.index ?? 0}%; operational ${domains.operational?.index ?? 0}%.`,
    priorityBlocks.length
      ? `Lowest-performing blocks needing review: ${blockText}.`
      : "Block-level variation is moderate; maintain preventive monitoring.",
  ];

  const recommendations = [
    "Launch a teacher-quality improvement sprint in blocks with the lowest CTET completion.",
    "Close APAAR and Aadhaar identity gaps where enrolment is high but compliance lags.",
    `Accelerate data certification in blocks below district average (${blockText}).`,
  ];

  const actions = [
    "Assign block officers to weekly closure reviews for the bottom five blocks by OSH score.",
    "Schedule a 30-day action sprint on the weakest domain index before the next district review.",
    "Link teacher staffing data with attendance and learning outcomes for targeted intervention.",
  ];

  return [
    "## Insights",
    `- ${narrative}`,
    `- Composite score ${shi} with ${rag} RAG status across four priority domains.`,
    "",
    "## Root Cause Signals",
    ...rootSignals.map((s) => `- ${s}`),
    "",
    "## Recommendations",
    ...recommendations.map((s) => `- ${s}`),
    "",
    "## Priority Action Items",
    ...actions.map((s) => `- ${s}`),
  ].join("\n");
};
